<template>
  <form id="futuro-customer-mounthly" class="my-5" @submit.prevent>
    <div class="card card-monthly-investment">
      <div class="row align-items-center justify-content-between">
        <div class="card-title col-auto mb-3">
          <img
            src="~@/assets/img/calendar.svg"
            alt="Futuro | Consultoria Financeira"
            style="width: 20px"
          />
          <h2 class="col-12">Investimento mensal</h2>
        </div>
        <spinner v-if="loading" class="mr-2" />
      </div>
      <div class="row">
        <p>
          Para alcançar metas, é necessário poupar com sucesso. Hoje, focado nos
          seus 3 principais objetivos,
          <b>quanto você consegue poupar com tranquilidade?</b>
        </p>
      </div>
      <div class="row mb-3 gy-3">
        <div class="col-md-4 col-12">
          <label for="valueMin" class="form-label"> Mínimo </label>
          <input
            id="valueMin"
            v-model.lazy="valueMin"
            v-money="moneyFormat"
            type="text"
            :disabled="isDetail"
            class="form-control"
            :class="{ 'is-invalid': v$.valueMin.$error }"
            @blur="preSave()"
          />
          <div class="invalid-feedback">
            <span v-if="v$.valueMin.minLength.$invalid">
              O valor é inválido
            </span>
          </div>
        </div>
        <div class="col-md-4 col-12">
          <label for="valueMax" class="form-label"> Máximo </label>
          <input
            id="valueMax"
            v-model.lazy="valueMax"
            v-money="moneyFormat"
            type="text"
            :disabled="isDetail"
            class="form-control"
            :class="{ 'is-invalid': v$.valueMax.$error }"
            @blur="preSave()"
          />
          <div class="invalid-feedback">
            <span v-if="v$.valueMax.minLength.$invalid">
              O valor é inválido
            </span>
          </div>
        </div>
        <div class="col-md-4 col-12">
          <label for="valueAvg" class="form-label"> Média </label>
          <input
            id="valueAvg"
            v-money="moneyFormat"
            :value="valueAvg"
            type="text"
            class="form-control"
            disabled
          />
        </div>
      </div>
      <template v-if="isInterview">
        <div class="row mb-3 gy-3">
          <div class="col-12">
            <label for="canStartNow" class="form-label">
              Trazendo um conceito focado nos objetivos citados e que se enquadre
              no seu perfil financeiro, você consegue começar desde já?
            </label>
            <select
              id="canStartNow"
              v-model="canStartNow"
              :disabled="isDetail"
              class="form-select"
              :class="{ 'select-disabled': isDetail }"
              @blur="preSave()"
            >
              <option value="yes">Sim</option>
              <option value="no">Não</option>
            </select>
          </div>
        </div>
        <div class="row mb-3 gy-3">
          <div class="col-12">
            <label for="predictedChange" class="form-label">
              Alguma mudança prevista na sua vida financeira nos próximos meses?
            </label>
            <textarea
              id="predictedChange"
              v-model="predictedChange"
              type="text"
              class="form-control"
              rows="4"
              :disabled="isDetail"
              @blur="preSave()"
            />
          </div>
        </div>
        <div class="row mb-3 gy-3">
          <div class="col-12">
            <label for="financialMattersObserved" class="form-label">
              Você vem acompanhando os assuntos financeiros nos últimos meses?
            </label>
            <select
              id="financialMattersObserved"
              v-model="financialMattersObserved"
              :disabled="isDetail"
              class="form-select"
              :class="{ 'select-disabled': isDetail }"
              @blur="preSave()"
            >
              <option value="yes">Sim</option>
              <option value="no">Não</option>
            </select>
          </div>
        </div>
        <comments-section
          v-if="interviewSection && token"
          :interview-section="interviewSection"
          :token="token"
        />
      </template>
    </div>
  </form>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { minLength } from "@vuelidate/validators";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { VMoney } from "v-money";
import {
  floatToMoney,
  floatToPercentage,
  percentageToFloat,
  booleanToString,
  moneyToFloat,
} from "@/helpers/converters";
import { getMonthlyInvestmentPayload } from "@/helpers/interview_payloads";
import * as types from "../store/types";
import Spinner from "@/modules/shared/components/Spinner";
import CommentsSection from "./CommentsSection";

const MIN_ANNUAL_RATE = 0;
const MAX_ANNUAL_RATE = 100;

function normalizeAnnualRate(value) {
  const parsedRate = Number(percentageToFloat(value));
  if (!Number.isFinite(parsedRate)) {
    return MIN_ANNUAL_RATE;
  }
  return Math.min(MAX_ANNUAL_RATE, Math.max(MIN_ANNUAL_RATE, parsedRate));
}

export default {
  components: {
    Spinner,
    CommentsSection,
  },
  directives: {
    money: VMoney,
  },
  props: {
    interviewSection: {
      type: String,
      default: "",
    },
    isInterview: {
      type: Boolean,
      default: false,
    },
    isDetail: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    return { v$: useVuelidate({ $registerAs: props.interviewSection }) };
  },
  data() {
    return {
      valueMin: "",
      valueMax: "",
      investmentRateAmateur: "",
      investmentRateProfessional: "",
      canStartNow: "",
      predictedChange: "",
      financialMattersObserved: "",
      token: "",
      loading: false,
    };
  },
  validations() {
    return {
      valueMin: {
        minLength: minLength(7),
      },
      valueMax: {
        minLength: minLength(7),
      },
      investmentRateAmateur: {
        minLength: minLength(7),
      },
      investmentRateProfessional: {
        minLength: minLength(7),
      },
      canStartNow: {},
      predictedChange: {},
      financialMattersObserved: {},
    };
  },
  computed: {
    ...mapGetters({
      formatedCurrency: types.FORMATED_CURRENCY,
    }),
    moneyFormat: function () {
      return this.formatedCurrency
    },
    valueAvg: function () {
      const hasMaximum = this.valueMax !== null
        && this.valueMax !== undefined
        && String(this.valueMax).trim() !== "";
      const hasMinimum = this.valueMin !== null
        && this.valueMin !== undefined
        && String(this.valueMin).trim() !== "";

      if (!hasMaximum && !hasMinimum) {
        return "";
      }

      const max = moneyToFloat(this.valueMax);
      const min = moneyToFloat(this.valueMin);
      if (hasMaximum && hasMinimum && max - min >= 0) {
        return floatToMoney((max + min) / 2);
      }
      if (hasMaximum && !hasMinimum) {
        return floatToMoney(max);
      }
      if (hasMinimum && !hasMaximum) {
        return floatToMoney(min);
      }
      return "";
    },
  },
  async mounted() {
    this.token = this.$route.params.customerId;
    await this.fetchAll();
  },
  methods: {
    ...mapActions({
      getMonthlyInvestment: types.GET_MONTHLY_INVESTMENT,
      autoSaveMonthlyInvestment: types.AUTO_SAVE_MONTHLY_INVESTMENT,
      getMonthlyInvestmentTaxes: types.GET_MONTHLY_INVESTMENT_TAXES,
    }),
    ...mapMutations({
      mutateMonthlyInvestment: types.MUTATE_MONTHLY_INVESTMENT,
    }),
    async fetchMonthlyInvestment() {
      this.loading = true;
      try {
        const response = await this.getMonthlyInvestment(
          this.$route.params.customerId
        );
        const {
          value_min,
          value_max,
          investment_rate_amateur,
          investment_rate_professional,
          predicted_change,
          financial_matters_observed,
          can_start_now,
        } = response.data || {};
        this.valueMin = value_min === null || value_min === undefined
          ? ""
          : floatToMoney(value_min);
        this.valueMax = value_max === null || value_max === undefined
          ? ""
          : floatToMoney(value_max);
        this.investmentRateAmateur = floatToPercentage(investment_rate_amateur);
        this.investmentRateProfessional = floatToPercentage(
          investment_rate_professional
        );
        this.normalizeRates();
        this.predictedChange = predicted_change;
        this.financialMattersObserved = booleanToString(
          financial_matters_observed
        );
        const payload = getMonthlyInvestmentPayload(this);
        this.mutateMonthlyInvestment(payload);
        if (
          (!investment_rate_amateur || investment_rate_amateur === 0) &&
          (!investment_rate_professional || investment_rate_professional === 0)
        ) {
          this.getTaxes();
        }
      } catch (error) {
        this.$vfm.show("message", {
          type: "error",
          messages: [
            "Não foi possível obter o investimento mensal do cliente.",
            "Entre em contato com o time de TI antes de prosseguir com a operação.",
          ],
        });
      } finally {
        this.loading = false;
      }
    },

    async fetchAll() {
      this.loading = true;
      try {
        await Promise.all([this.fetchMonthlyInvestment()]);
      } catch (error) {
      } finally {
        this.loading = false;
      }
    },

    preSave() {
      this.normalizeRates();
      const payload = getMonthlyInvestmentPayload(this);
      const currentPayload = JSON.stringify(payload);
      if (this.tempPayload !== currentPayload) {
        this.tempPayload = currentPayload;
        this.mutateMonthlyInvestment(payload);
        this.autoSave(payload);
      }
    },
    autoSave(payload) {
      this.loading = true;
      this.autoSaveMonthlyInvestment(payload)
        .catch((error) => {
          this.$vfm.show("message", {
            type: "error",
            messages: [
              "Não foi possível salvar automaticamente o investimento mensal do cliente.",
              "Entre em contato com o time de TI antes de prosseguir com a operação.",
            ],
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getTaxes() {
      this.getMonthlyInvestmentTaxes()
        .then((response) => {
          const {
            default_investment_rate_amateur,
            default_investment_rate_professional,
          } = response.data;
          this.investmentRateAmateur = floatToPercentage(
            default_investment_rate_amateur
          );
          this.investmentRateProfessional = floatToPercentage(
            default_investment_rate_professional
          );
          this.normalizeRates();
        })
        .catch((error) => {
          this.$vfm.show("message", {
            type: "error",
            messages: [
              "Não foi possível obter as taxas de investimento mensal.",
              "Entre em contato com o time de TI antes de prosseguir com a operação.",
            ],
          });
        });
    },
    normalizeRateField(fieldName) {
      const normalizedRate = normalizeAnnualRate(this[fieldName]);
      const normalizedText = floatToPercentage(normalizedRate);
      if (this[fieldName] !== normalizedText) {
        this[fieldName] = normalizedText;
      }
      return normalizedRate;
    },
    normalizeRates() {
      this.normalizeRateField("investmentRateAmateur");
      this.normalizeRateField("investmentRateProfessional");
    },
  },
};
</script>

<style lang="scss" scoped>
p {
  padding: 12px;
  margin-bottom: 0;
}
/*Ajuste no grafico de investimentos*/
@media (max-width: 768px) {
  /*Correção dos campos de texto minisculos no mobile (fluxo de caixa)*/
  .row.align-items-center.mb-3 .col-md-4.col-6 {
    min-width: 100% !important;
  }
  /*Correção de parte do scroll horizontal no mobile*/
  .h-100.inner-section > .ribbon {
    max-width: 100% !important;
    padding: 0;
  }
}
</style>
